/**
 * PERFILES LOCALES (FASE 5.6 §7-§14, §26-§29)
 *
 * Directorio de perfiles offline de ESTE dispositivo/navegador — separado
 * a propósito del almacén de datos de cada perfil (`db.ts`, que sigue
 * siendo "una IndexedDB por perfil", ahora nombrada por `perfilId` en vez
 * de por `idUsuario`, ver más abajo). Este módulo es el ÚNICO sitio que:
 *
 *   · sabe qué perfiles locales existen en este dispositivo (§9/§26);
 *   · sabe qué perfil está activo y si está bloqueado (§13);
 *   · guarda la DEK en memoria durante el rato que el perfil está
 *     desbloqueado — NUNCA en IndexedDB/localStorage/sessionStorage (§14).
 *
 * NO SUSTITUYE AUTENTICACIÓN (§C/§10)
 *
 * Nada de aquí valida al usuario frente al backend. `crearPerfilLocal`
 * recibe un `idUsuario` ya autenticado por el flujo normal de login — este
 * módulo sólo decide con qué perfil LOCAL corresponde ese `idUsuario` en
 * ESTE dispositivo, vía huella (§8), y protege/desprotege su DEK con un
 * PIN que nunca sale de aquí ni se envía a ningún sitio.
 *
 * METADATA MÍNIMA EN CLARO (§7)
 *
 * `PerfilLocal` es exactamente la lista que autoriza el encargo:
 * `perfilId`, `fingerprintUsuario` (nunca `idUsuario` en claro),
 * `aliasLocal` (nunca el nombre/correo real por defecto), los parámetros
 * de cifrado, y dos timestamps técnicos. Nada de cliente/centro/rol/datos
 * operativos vive en esta fila.
 */
import { useEffect, useState } from 'react';

import {
  cambiarPin as cambiarPinDeLaDek,
  crearEnvolturaDek,
  desenvolverDek,
  ErrorDescifrado,
  type EnvolturaDek,
} from './crypto.ts';

const NOMBRE_DIRECTORIO = 'neatos-perfiles';
const VERSION_DIRECTORIO = 1;
const STORE_PERFILES = 'perfiles';
const CLAVE_ULTIMO_PERFIL = 'neatos.ultimoPerfilId';
const NAMESPACE_FINGERPRINT = 'neatos:fingerprint-usuario:v1:';

export interface PerfilLocal extends EnvolturaDek {
  readonly perfilId: string;
  readonly fingerprintUsuario: string;
  readonly aliasLocal: string;
  readonly creadoEn: string;
  readonly ultimoUsoEn: string;
}

/** Nombre físico de la IndexedDB de datos de un perfil — SIEMPRE por `perfilId`, nunca por `idUsuario` (§7/§18: nunca un identificador real de negocio en el nombre de la base). */
export function nombreBaseDeDatosDePerfil(perfilId: string): string {
  return `neatos-offline-cripto-${perfilId}`;
}

// -------------------------------------------------------------- huella (§8)

/** SHA-256(namespace + idUsuario), en hexadecimal — nunca se guarda `idUsuario` en claro; esto es lo que se compara para encontrar el perfil local de un usuario ya autenticado online. */
export async function fingerprintDeUsuario(idUsuario: string): Promise<string> {
  const bytes = new TextEncoder().encode(NAMESPACE_FINGERPRINT + idUsuario);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------- directorio idb

function promesa<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Petición IndexedDB fallida.'));
  });
}

function promesaTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Transacción IndexedDB fallida.'));
    tx.onabort = () => reject(tx.error ?? new Error('Transacción IndexedDB abortada.'));
  });
}

function abrirDirectorio(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const peticion = indexedDB.open(NOMBRE_DIRECTORIO, VERSION_DIRECTORIO);
    peticion.onupgradeneeded = () => {
      const db = peticion.result;
      if (!db.objectStoreNames.contains(STORE_PERFILES)) {
        db.createObjectStore(STORE_PERFILES, { keyPath: 'perfilId' }).createIndex(
          'fingerprintUsuario',
          'fingerprintUsuario',
        );
      }
    };
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido abrir el directorio de perfiles.'));
  });
}

/** Lista para el selector (§9) — sólo alias, nunca el correo/nombre real (no vive aquí de todos modos). */
export async function listarPerfilesLocales(): Promise<PerfilLocal[]> {
  const db = await abrirDirectorio();
  try {
    const tx = db.transaction(STORE_PERFILES, 'readonly');
    return (await promesa(tx.objectStore(STORE_PERFILES).getAll())) as PerfilLocal[];
  } finally {
    db.close();
  }
}

export async function buscarPerfilPorFingerprint(fingerprintUsuario: string): Promise<PerfilLocal | null> {
  const db = await abrirDirectorio();
  try {
    const tx = db.transaction(STORE_PERFILES, 'readonly');
    const filas = (await promesa(
      tx.objectStore(STORE_PERFILES).index('fingerprintUsuario').getAll(fingerprintUsuario),
    )) as PerfilLocal[];
    return filas[0] ?? null;
  } finally {
    db.close();
  }
}

async function leerPerfil(perfilId: string): Promise<PerfilLocal | null> {
  const db = await abrirDirectorio();
  try {
    const tx = db.transaction(STORE_PERFILES, 'readonly');
    const fila = (await promesa(tx.objectStore(STORE_PERFILES).get(perfilId))) as PerfilLocal | undefined;
    return fila ?? null;
  } finally {
    db.close();
  }
}

async function guardarPerfil(perfil: PerfilLocal): Promise<void> {
  const db = await abrirDirectorio();
  try {
    const tx = db.transaction(STORE_PERFILES, 'readwrite');
    tx.objectStore(STORE_PERFILES).put(perfil);
    await promesaTx(tx);
  } finally {
    db.close();
  }
}

// --------------------------------------------------- último perfil (§26)

/** UUID local opaco únicamente — nunca información de negocio (§26: "puede existir si es UUID local opaco"). */
export function ultimoPerfilId(): string | null {
  try {
    return localStorage.getItem(CLAVE_ULTIMO_PERFIL);
  } catch {
    return null;
  }
}

function fijarUltimoPerfilId(perfilId: string | null): void {
  try {
    if (perfilId === null) localStorage.removeItem(CLAVE_ULTIMO_PERFIL);
    else localStorage.setItem(CLAVE_ULTIMO_PERFIL, perfilId);
  } catch {
    /* almacenamiento bloqueado: se preguntará de nuevo qué perfil abrir la próxima vez. */
  }
}

// ------------------------------------------------- estado de bloqueo (§13)

export type EstadoBloqueoOffline =
  | { readonly estado: 'LOCKED'; readonly perfilId: string; readonly aliasLocal: string }
  | { readonly estado: 'UNLOCKED'; readonly perfilId: string; readonly aliasLocal: string };

type Escucha = (estado: EstadoBloqueoOffline | null) => void;
const escuchas = new Set<Escucha>();

/**
 * `null` = no hay perfil activo seleccionado en esta sesión de pestaña
 * (nunca visto ninguno, o se acaba de "cambiar de perfil" sin elegir
 * otro todavía). Nunca se confunde con LOCKED: LOCKED significa "hay un
 * perfil concreto esperando PIN", `null` significa "todavía no se sabe
 * cuál".
 */
let activo: { readonly perfil: PerfilLocal; readonly dek: CryptoKey } | null = null;
let estadoNotificado: EstadoBloqueoOffline | null = null;

function notificar(): void {
  const siguiente: EstadoBloqueoOffline | null =
    activo === null
      ? estadoNotificado?.estado === 'LOCKED'
        ? estadoNotificado
        : null
      : { estado: 'UNLOCKED', perfilId: activo.perfil.perfilId, aliasLocal: activo.perfil.aliasLocal };
  estadoNotificado = siguiente;
  for (const f of escuchas) f(siguiente);
}

export function estadoBloqueoActual(): EstadoBloqueoOffline | null {
  return estadoNotificado;
}

export function suscribirEstadoBloqueo(f: Escucha): () => void {
  escuchas.add(f);
  return () => {
    escuchas.delete(f);
  };
}

/** Marca explícitamente "hay que pedir PIN para este perfil" — lo llama la UI al reconocer, vía huella, cuál es el último perfil de este dispositivo (§26: nunca se desbloquea solo). */
function fijarLocked(perfil: PerfilLocal): void {
  activo = null;
  estadoNotificado = { estado: 'LOCKED', perfilId: perfil.perfilId, aliasLocal: perfil.aliasLocal };
  for (const f of escuchas) f(estadoNotificado);
}

/** Sólo para la pantalla de selección/desbloqueo: prepara el estado LOCKED de un perfil concreto antes de pedir el PIN. */
export async function prepararDesbloqueo(perfilId: string): Promise<PerfilLocal | null> {
  const perfil = await leerPerfil(perfilId);
  if (perfil === null) return null;
  fijarLocked(perfil);
  return perfil;
}

/**
 * FASE 5.7-C C11 — limpia un LOCKED huérfano de una identidad ANTERIOR.
 *
 * `fijarLocked`/`bloquearPerfilActivo` dejan `estadoNotificado` apuntando
 * a un `perfilId` concreto, y NADA lo borra por su cuenta — sigue ahí
 * hasta que otra llamada a `fijarLocked` lo sustituya. Eso es exactamente
 * lo que rompía C11: Pepe hace logout (`bloquearPerfilActivo` dispara
 * `fijarLocked(Pepe)`), Eva inicia sesión SIN recargar la página (misma
 * SPA) y no tiene perfil propio en este dispositivo — la puerta
 * (`PuertaOfflineSegura`) resuelve correctamente "sin perfil para Eva",
 * pero nunca tocaba `estadoNotificado`, que seguía señalando al perfil de
 * Pepe: la pantalla de PIN que se veía era la de Pepe, no la de "crear
 * perfil de Eva". `prepararDesbloqueo` YA sustituye correctamente este
 * estado cuando SÍ hay un perfil que ofrecer (para la identidad actual);
 * esta función cubre el caso contrario, que antes no limpiaba nada.
 *
 * Deliberadamente NO toca `activo` (perfil de verdad desbloqueado, con
 * DEK en memoria) — ese caso ya lo gestiona `bloquearPerfilActivo`, y
 * mezclar los dos aquí arriesgaría bloquear un perfil que sigue siendo
 * válido para la identidad actual.
 */
export function limpiarPerfilBloqueadoSiNoHayActivo(): void {
  if (activo !== null) return;
  if (estadoNotificado === null) return;
  estadoNotificado = null;
  for (const f of escuchas) f(null);
}

// ----------------------------------------------------------- ciclo de vida

/**
 * FASE 5.7-C C09 — mejora progresiva, nunca crítica: pide al navegador que
 * NO trate `neatos-perfiles`/las bases por perfil como "borrables bajo
 * presión de espacio" (Storage API). Best-effort a propósito: en Safari
 * puede no existir, denegar sin preguntar, o requerir interacción previa —
 * ninguno de esos casos debe romper el alta/desbloqueo, por eso nunca se
 * espera ni se propaga su resultado a quien llama.
 */
function pedirAlmacenamientoPersistente(): void {
  void navigator.storage?.persist?.().catch(() => undefined);
}

/**
 * Alta de un perfil local nuevo (§7/§10, "Configurar acceso offline").
 * Deja el perfil UNLOCKED de inmediato (el usuario acaba de teclear el
 * PIN dos veces, exigir un tercer tecleo sería fricción sin ganancia de
 * seguridad) y lo recuerda como `ultimoPerfilId`.
 */
export async function crearPerfilLocal(idUsuario: string, pin: string, aliasLocal: string): Promise<PerfilLocal> {
  const fingerprintUsuario = await fingerprintDeUsuario(idUsuario);
  const existente = await buscarPerfilPorFingerprint(fingerprintUsuario);
  if (existente !== null) {
    throw new Error('Ya existe un perfil local para este usuario en este dispositivo.');
  }

  const { envoltura, dek } = await crearEnvolturaDek(pin);
  const ahora = new Date().toISOString();
  const perfil: PerfilLocal = {
    ...envoltura,
    perfilId: crypto.randomUUID(),
    fingerprintUsuario,
    aliasLocal,
    creadoEn: ahora,
    ultimoUsoEn: ahora,
  };
  await guardarPerfil(perfil);

  activo = { perfil, dek };
  fijarUltimoPerfilId(perfil.perfilId);
  pedirAlmacenamientoPersistente();
  notificar();
  return perfil;
}

/**
 * Desbloqueo (§11). `ErrorDescifrado` en PIN incorrecto — la UI la
 * traduce SIEMPRE al mismo mensaje genérico, nunca distingue de otro
 * fallo (ver `crypto.ts`). Nunca se toca ni borra el perfil en un fallo.
 */
export async function desbloquearPerfil(perfilId: string, pin: string): Promise<PerfilLocal> {
  const perfil = await leerPerfil(perfilId);
  if (perfil === null) throw new ErrorDescifrado();
  const dek = await desenvolverDek(pin, perfil); // lanza ErrorDescifrado si el PIN no corresponde

  const actualizado: PerfilLocal = { ...perfil, ultimoUsoEn: new Date().toISOString() };
  await guardarPerfil(actualizado); // mejor esfuerzo: si falla, sólo se pierde el timestamp de uso

  activo = { perfil: actualizado, dek };
  fijarUltimoPerfilId(perfilId);
  pedirAlmacenamientoPersistente();
  notificar();
  return actualizado;
}

/**
 * Bloqueo (§14/§15/§27): borra la referencia a la DEK de memoria — nunca
 * queda una copia accesible tras esta llamada. Se usa tanto para "Bloquear"
 * explícito como para logout (§15) y "Cambiar perfil" (§27); quien llama
 * decide si además navega a otra pantalla o limpia otro estado (React,
 * sync, URLs de objeto) — este módulo sólo es responsable de la DEK y del
 * estado de bloqueo.
 */
export function bloquearPerfilActivo(): void {
  if (activo === null) return;
  const perfil = activo.perfil;
  activo = null;
  fijarLocked(perfil);
}

/** Perfil activo ahora mismo, o `null` si no hay ninguno desbloqueado. Nunca expone la DEK a quien no la necesite operar — sólo `db.ts` la pide. */
export function perfilActivo(): PerfilLocal | null {
  return activo?.perfil ?? null;
}

/**
 * Tipo dedicado (nunca `Error` genérico) para que quien llama pueda
 * distinguir "el perfil está bloqueado ahora mismo" de un fallo real —
 * FASE 5.6 §16: una sincronización en vuelo cuyo perfil se bloquea a
 * mitad (logout, bloqueo manual, cambio de perfil) debe abortar en
 * silencio, igual criterio que un fallo de red (§18) — nunca un
 * `unhandledrejection`, y nunca un intento de "deshacer" lo que el
 * servidor ya pudiera haber aplicado (no hay rollback cliente; la
 * reconciliación real llega en el siguiente desbloqueo, por idempotencia).
 * Ver `offline/sync.ts`.
 */
export class ErrorPerfilBloqueado extends Error {
  constructor() {
    super('El perfil offline está bloqueado.');
    this.name = 'ErrorPerfilBloqueado';
  }
}

/**
 * La DEK activa, SÓLO si corresponde al `perfilId` que pide quien llama —
 * defensa en profundidad además del AAD (§18): un error de programación
 * que pasara el `perfilId` equivocado a `db.ts` falla aquí, explícito,
 * antes de intentar ningún cifrado/descifrado.
 */
export function dekActivaOLanzar(perfilId: string): CryptoKey {
  if (activo === null || activo.perfil.perfilId !== perfilId) {
    throw new ErrorPerfilBloqueado();
  }
  return activo.dek;
}

export function hayPerfilDesbloqueado(perfilId: string): boolean {
  return activo !== null && activo.perfil.perfilId === perfilId;
}

// ------------------------------------------------------- cambio de pin (§12)

export async function cambiarPinPerfil(perfilId: string, pinActual: string, pinNuevo: string): Promise<void> {
  const perfil = await leerPerfil(perfilId);
  if (perfil === null) throw new ErrorDescifrado();
  const nuevaEnvoltura = await cambiarPinDeLaDek(pinActual, perfil, pinNuevo); // lanza ErrorDescifrado si pinActual no corresponde

  const actualizado: PerfilLocal = { ...perfil, ...nuevaEnvoltura, ultimoUsoEn: new Date().toISOString() };
  await guardarPerfil(actualizado);

  //  Si este perfil era el activo, se refresca la fila en memoria (misma
  //  DEK, nunca hace falta volver a desbloquear) — ver `crypto.ts::cambiarPin`.
  if (activo !== null && activo.perfil.perfilId === perfilId) {
    activo = { ...activo, perfil: actualizado };
  }
}

// --------------------------------------------------- eliminar perfil (§28)

/**
 * Elimina EXCLUSIVAMENTE este perfil: su fila de directorio y su
 * IndexedDB de datos. Nunca toca el servidor ni otros perfiles (§28). Si
 * era el perfil activo, primero se bloquea (borra la DEK de memoria) para
 * que no quede una referencia viva a datos que ya no existen.
 *
 * FASE 5.7-C H15-F — hallazgo de la revisión adversarial: borrar un perfil
 * que está LOCKED pero nunca se llegó a desbloquear esta sesión (el caso
 * exacto de "He olvidado mi PIN" -> "Eliminar perfil local y empezar de
 * nuevo", `PantallaPinOlvidado`) NO pasa por la rama de arriba (`activo`
 * es `null` — nunca hubo DEK que borrar) y antes no tocaba
 * `estadoNotificado` en absoluto: la pantalla de PIN seguía señalando al
 * `perfilId` recién borrado, un perfil FANTASMA para el que NINGÚN PIN
 * puede volver a descifrar nada nunca (`leerPerfil` devuelve `null` ->
 * `ErrorDescifrado` siempre) — exactamente el bucle sin salida que H15
 * pide evitar. Mismo criterio que `limpiarPerfilBloqueadoSiNoHayActivo`
 * (C11): nunca dejar `estadoNotificado` apuntando a un perfil que ya no
 * tiene razón de estar ahí.
 */
export async function eliminarPerfilLocal(perfilId: string): Promise<void> {
  if (activo !== null && activo.perfil.perfilId === perfilId) {
    activo = null;
    estadoNotificado = null;
    for (const f of escuchas) f(null);
  } else if (estadoNotificado !== null && estadoNotificado.perfilId === perfilId) {
    estadoNotificado = null;
    for (const f of escuchas) f(null);
  }

  const db = await abrirDirectorio();
  try {
    const tx = db.transaction(STORE_PERFILES, 'readwrite');
    tx.objectStore(STORE_PERFILES).delete(perfilId);
    await promesaTx(tx);
  } finally {
    db.close();
  }

  await new Promise<void>((resolve, reject) => {
    const peticion = indexedDB.deleteDatabase(nombreBaseDeDatosDePerfil(perfilId));
    peticion.onsuccess = () => resolve();
    peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido eliminar el almacén del perfil.'));
    peticion.onblocked = () => resolve(); // otra pestaña la tiene abierta — se completará cuando la cierre; nunca bloquear esta acción por eso.
  });

  if (ultimoPerfilId() === perfilId) fijarUltimoPerfilId(null);
}

// ---------------------------------------------------------------- hook react

/** Para que cualquier pantalla reaccione a bloqueo/desbloqueo/cambio de perfil sin orquestarlo ella misma — mismo patrón que `useEstadoSync` (`offline/sync.ts`). */
export function useEstadoBloqueoOffline(): EstadoBloqueoOffline | null {
  const [estado, setEstado] = useState(estadoBloqueoActual());
  useEffect(() => suscribirEstadoBloqueo(setEstado), []);
  return estado;
}
